import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FiCalendar, FiMapPin } from 'react-icons/fi';

const EducationSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.8rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #00ff00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #00ff00;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 2.5rem;
  }
`;

const EducationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 2.5rem;
  }
`;

const EducationCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 0, 0.2);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba(0, 255, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem;
    border-radius: 16px;
  }
`;

const DegreeTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const InstitutionName = styled.h4`
  font-size: 1.2rem;
  font-weight: 500;
  color: #00ff00;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const EducationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const Description = styled.p`
  color: #cbd5e1;
  line-height: 1.6;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    line-height: 1.7;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.8;
  }
`;

const Education = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const educationData = [
    {
      degree: 'Bachelor of Science in Computer Science',
      institution: 'FAST-NUCES',
      location: 'Lahore, Pakistan',
      period: 'Aug 2023 - Present',
      description: 'Currently pursuing a comprehensive Computer Science degree with focus on programming fundamentals, algorithms, data structures, and modern software development practices.'
    },
    {
      degree: 'FSC Pre-Engineering',
      institution: 'Punjab College',
      location: 'Sargodha, Pakistan',
      period: 'Jul 2021 - Feb 2023',
      description: 'Completed pre-engineering studies with strong foundation in mathematics, physics, and chemistry, preparing for higher education in technical fields.'
    },
    {
      degree: 'Matriculation (Science)',
      institution: 'Sanai School System',
      location: 'Sargodha, Pakistan',
      period: 'Jun 2020',
      description: 'Completed secondary school education with a science major, building strong analytical skills.'
    }
  ];

  return (
    <EducationSection id="education" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Education
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          My academic journey and educational background
        </SectionSubtitle>

        <EducationGrid>
          {educationData.map((education, index) => (
            <EducationCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <DegreeTitle>{education.degree}</DegreeTitle>
              <InstitutionName>{education.institution}</InstitutionName>
              
              <EducationMeta>
                <MetaItem>
                  <FiCalendar />
                  {education.period}
                </MetaItem>
                <MetaItem>
                  <FiMapPin />
                  {education.location}
                </MetaItem>
              </EducationMeta>
              
              <Description>{education.description}</Description>
            </EducationCard>
          ))}
        </EducationGrid>
      </Container>
    </EducationSection>
  );
};

export default Education;
